import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Play, Square } from "lucide-react";
import { API } from "@/App";
import { useTranslation } from "@/i18n/translations";

export default function VoiceReplySettings() {
  const { t } = useTranslation();
  const [settings, setSettingsState] = useState({ voice_reply_mode: "off", voice_id: null });
  const [salonId, setSalonId] = useState(null);
  const setSettings = (next) => {
    setSettingsState(next);
    if (salonId) axios.put(`${API}/salons/${salonId}`, { voice_reply_mode: next.voice_reply_mode, voice_id: next.voice_id })
      .then(() => toast.success("OK")).catch(() => toast.error("Error"));
  };
  useEffect(() => {
    axios.get(`${API}/salons`).then((r) => {
      const sl = (r.data || [])[0];
      if (!sl) return;
      setSalonId(sl.id);
      setSettingsState({ voice_reply_mode: sl.voice_reply_mode || "off", voice_id: sl.voice_id || null });
    }).catch(() => {});
  }, []);
  const [voices, setVoices] = useState(null);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/voice/voices`).then((r) => setVoices(r.data)).catch(() => setVoices({ configured: false, voices: [] }));
  }, []);

  const stop = () => { audioRef.current?.pause(); audioRef.current = null; setPlaying(false); };
  const preview = async () => {
    if (playing) return stop();
    try {
      setPlaying(true);
      const r = await axios.post(`${API}/voice/preview`, { voice_id: settings.voice_id || voices?.default }, { responseType: "blob" });
      const a = new Audio(URL.createObjectURL(r.data));
      audioRef.current = a;
      a.onended = () => setPlaying(false);
      await a.play();
    } catch { toast.error("Preview failed"); setPlaying(false); }
  };

  if (!voices || !voices.configured) return null;
  const mode = settings.voice_reply_mode || "off";
  return (
    <div className="mt-4 pt-3 border-t border-gray-200" data-testid="voice-reply-block">
      <h4 className="font-bold text-black mb-1 text-sm">🎙 {t("settings.voiceTitle")}</h4>
      <p className="text-xs text-gray-600 mb-2">{t("settings.voiceHint")}</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="voice-mode-selector">
        {["off", "voice_in", "always"].map((m) => (
          <button key={m} type="button" data-testid={`voice-mode-${m}`}
            onClick={() => setSettings({ ...settings, voice_reply_mode: m })}
            className={`text-left p-3 rounded-xl border-2 transition-colors ${mode === m ? "border-black bg-black text-white" : "border-gray-200 bg-white hover:border-gray-400"}`}>
            <div className="text-sm font-bold">{t(`settings.voiceMode_${m}`)}</div>
            <div className={`text-xs mt-1 ${mode === m ? "text-gray-200" : "text-gray-500"}`}>{t(`settings.voiceMode_${m}_desc`)}</div>
          </button>
        ))}
      </div>
      {mode !== "off" && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <select data-testid="voice-select" value={settings.voice_id || voices.default}
            onChange={(e) => { stop(); setSettings({ ...settings, voice_id: e.target.value }); }}
            className="border-2 border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:border-black outline-none w-full sm:w-80">
            {voices.voices.map((v) => (
              <option key={v.voice_id} value={v.voice_id}>{v.name}{v.gender ? ` · ${v.gender}` : ""}{v.description ? ` — ${v.description}` : ""}</option>
            ))}
          </select>
          <button type="button" onClick={preview} data-testid="voice-preview-btn"
            className="flex items-center gap-1 text-xs font-semibold px-3 py-2 rounded-xl border-2 border-black bg-white hover:bg-gray-50">
            {playing ? <Square size={12} /> : <Play size={12} />} {playing ? t("settings.voiceStop") : t("settings.voicePreview")}
          </button>
          {voices.quota?.limit && (
            <span className="text-[11px] text-gray-400 ml-auto">{t("settings.voiceQuota")}: {voices.quota.used?.toLocaleString()} / {voices.quota.limit?.toLocaleString()}</span>
          )}
        </div>
      )}
    </div>
  );
}
